import {
  useGrippContext,
  useTheme,
  useUpdateTheme,
  type ThemePreference,
} from '@gripp/shared-logic';
import { Colors, tokens, useSlideLayout } from '@gripp/shared-ui';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Circle, Polygon, Rect, Svg } from 'react-native-svg';

type Option = {
  value: ThemePreference;
  labelKey: string;
  fallback: string;
  descriptionKey: string;
  descriptionFallback: string;
};

const OPTIONS: Option[] = [
  {
    value: 'system',
    labelKey: 'preferences.appearance.system',
    fallback: 'System',
    descriptionKey: 'preferences.appearance.systemDescription',
    descriptionFallback: 'Match your operating system setting.',
  },
  {
    value: 'light',
    labelKey: 'preferences.appearance.light',
    fallback: 'Light',
    descriptionKey: 'preferences.appearance.lightDescription',
    descriptionFallback: 'Default Crop Circle palette.',
  },
  {
    value: 'dark',
    labelKey: 'preferences.appearance.dark',
    fallback: 'Dark',
    descriptionKey: 'preferences.appearance.darkDescription',
    descriptionFallback: 'Reduced-eye-strain palette for low-light use.',
  },
];

/**
 * The preview swatches depict the themes, so they are the one place in the app
 * whose colours must NOT follow the current theme: the Light card has to look
 * light while the app is dark, or it stops being a preview. These are the same
 * literals the Gripp OS page uses.
 */
const PREVIEW = {
  light: { page: '#FCFCFC', card: '#ECEEF1', text: '#1A1D24' },
  dark: { page: '#141414', card: '#1C1C1C', text: '#FCFCFC' },
} as const;

/**
 * Radio, matching Gripp OS exactly: a light dial in both modes, because it sits
 * on the preview card rather than on the page.
 */
const Radio = ({ selected }: { selected: boolean }) => (
  <Svg width={20} height={20} viewBox="0 0 20 20" fill="none">
    <Rect x="1" y="1" width="18" height="18" rx="9" fill="#FCFCFC" />
    <Rect
      x="1"
      y="1"
      width="18"
      height="18"
      rx="9"
      stroke={selected ? '#3A3F4D' : '#7A8394'}
      strokeWidth="1.5"
    />
    {selected && <Circle cx="10" cy="10" r="5" fill="#3A3F4D" />}
  </Svg>
);

const PREVIEW_WIDTH = 96;
const PREVIEW_HEIGHT = 56;

/**
 * Mini-mockup so the choice is visible before it is committed.
 *
 * `system` is the light preview with the dark one over its bottom-right
 * triangle - the diagonal split the OS page draws with a CSS clip-path. React
 * Native has no clip-path, so the half is an SVG polygon on the frame's exact
 * corners: no trigonometry, and it works on native too. Like the OS version it
 * shows only the split and one row pair per half - no top bar and no brand dot,
 * which would land on the seam.
 */
const ThemePreview = ({ variant }: { variant: ThemePreference }) => {
  if (variant === 'system') {
    return (
      <View style={[styles.preview, { backgroundColor: PREVIEW.light.page }]}>
        <Svg
          width={PREVIEW_WIDTH}
          height={PREVIEW_HEIGHT}
          style={StyleSheet.absoluteFill}
        >
          <Polygon
            points={`${PREVIEW_WIDTH},0 ${PREVIEW_WIDTH},${PREVIEW_HEIGHT} 0,${PREVIEW_HEIGHT}`}
            fill={PREVIEW.dark.page}
          />
        </Svg>

        <View style={styles.previewRows}>
          <View
            style={[
              styles.previewRowWide,
              { backgroundColor: PREVIEW.light.text, opacity: 0.85 },
            ]}
          />
          <View
            style={[
              styles.previewRowNarrow,
              { backgroundColor: PREVIEW.light.text, opacity: 0.4 },
            ]}
          />
        </View>

        <View style={styles.previewRowsInverse}>
          <View
            style={[
              styles.previewRowNarrow,
              { backgroundColor: PREVIEW.dark.text, opacity: 0.4 },
            ]}
          />
          <View
            style={[
              styles.previewRowWide,
              { backgroundColor: PREVIEW.dark.text, opacity: 0.85 },
            ]}
          />
        </View>
      </View>
    );
  }

  const base = variant === 'dark' ? PREVIEW.dark : PREVIEW.light;

  return (
    <View style={[styles.preview, { backgroundColor: base.page }]}>
      <View style={[styles.previewBar, { backgroundColor: base.card }]} />

      <View style={styles.previewRows}>
        <View
          style={[
            styles.previewRowWide,
            { backgroundColor: base.text, opacity: 0.85 },
          ]}
        />
        <View
          style={[
            styles.previewRowNarrow,
            { backgroundColor: base.text, opacity: 0.4 },
          ]}
        />
      </View>

      <View style={styles.previewDot} />
    </View>
  );
};

/**
 * Settings -> Appearance, matching the Gripp OS page it was designed against.
 *
 * Selection takes effect immediately rather than on save, because the change is
 * its own preview - a Save button would just be a second click to confirm
 * something the user can already see.
 */
export const SettingsAppearance = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const updateTheme = useUpdateTheme();
  const { logger } = useGrippContext();
  const { setColumnTitle } = useSlideLayout();

  useEffect(() => {
    setColumnTitle?.(t('preferences.settings.appearance', 'Appearance'));
  }, [setColumnTitle, t]);

  // The local half of `updateTheme` is synchronous, so the palette has already
  // flipped by the time the account write can fail. Log rather than revert:
  // yanking the theme back out from under the user would be worse than a
  // preference that does not follow them to their next device.
  const onSelect = useCallback(
    async (next: ThemePreference) => {
      try {
        await updateTheme(next);
      } catch (error) {
        logger?.error(error, 'Failed to save the appearance preference');
      }
    },
    [logger, updateTheme]
  );

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>
          {t('preferences.appearance.title', 'Theme')}
        </Text>

        <View style={styles.grid}>
          {OPTIONS.map((option) => {
            const selected = theme === option.value;
            return (
              <Pressable
                key={option.value}
                onPress={() => {
                  // `onSelect` handles its own failures, so nothing is lost by
                  // not awaiting here.
                  void onSelect(option.value);
                }}
                testID={`appearance-option-${option.value}`}
                accessibilityRole="radio"
                accessibilityState={{ selected }}
                accessibilityLabel={t(option.labelKey, option.fallback)}
                style={[styles.card, selected && styles.cardSelected]}
              >
                <ThemePreview variant={option.value} />
                <View style={styles.cardLabels}>
                  <Text style={styles.cardTitle}>
                    {t(option.labelKey, option.fallback)}
                  </Text>
                  <Text style={styles.cardDescription}>
                    {t(option.descriptionKey, option.descriptionFallback)}
                  </Text>
                </View>
                <Radio selected={selected} />
              </Pressable>
            );
          })}
        </View>
      </View>

      {/* Placeholder section so future options (font size, density, reduced
          motion) slot in without rethinking the page shape. Drop the
          empty-state once the first one lands. */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>
          {t('preferences.appearance.more', 'More')}
        </Text>
        <View style={styles.placeholder}>
          <Text style={styles.placeholderText}>
            {t(
              'preferences.appearance.moreComingSoon',
              'Font size, density and reduced-motion controls coming soon.'
            )}
          </Text>
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: tokens.spacing.xl,
    gap: tokens.spacing['3xl'],
  },
  section: {
    gap: tokens.spacing.lg,
  },
  sectionTitle: {
    ...tokens.typography.label.largeEmphasised,
    color: Colors.role.textStrong,
  },
  // Full-width rows rather than a three-up grid: the descriptions are a full
  // sentence each, and at a third of the panel they wrapped to three lines and
  // squeezed the preview. A row gives the text its width back and keeps the
  // thumbnail legible, matching the Activity card's shape.
  grid: {
    gap: tokens.spacing.md,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.lg,
    padding: tokens.spacing.md,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.role.borderSection,
  },
  cardSelected: {
    borderColor: Colors.role.textStrong,
    backgroundColor: Colors.role.surfaceRaised,
  },
  cardLabels: {
    flex: 1,
    gap: 2,
  },
  cardTitle: {
    ...tokens.typography.label.largeEmphasised,
    color: Colors.role.textStrong,
  },
  cardDescription: {
    ...tokens.typography.label.medium,
    color: Colors.role.textSoft,
  },

  preview: {
    width: PREVIEW_WIDTH,
    height: PREVIEW_HEIGHT,
    flexShrink: 0,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: Colors.role.borderSection,
    overflow: 'hidden',
  },
  previewBar: {
    position: 'absolute',
    top: 6,
    left: 6,
    right: 6,
    height: 9,
    borderRadius: 2,
  },
  previewRows: {
    position: 'absolute',
    left: 6,
    top: 20,
    gap: 4,
  },
  previewRowsInverse: {
    position: 'absolute',
    right: 6,
    bottom: 6,
    gap: 4,
    alignItems: 'flex-end',
  },
  previewRowWide: {
    height: 5,
    width: 30,
    borderRadius: 999,
  },
  previewRowNarrow: {
    height: 3,
    width: 18,
    borderRadius: 999,
  },
  previewDot: {
    position: 'absolute',
    right: 6,
    bottom: 6,
    height: 9,
    width: 9,
    borderRadius: 999,
    backgroundColor: tokens.colors.brand.core,
  },

  placeholder: {
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: Colors.role.borderSection,
    padding: tokens.spacing.lg,
  },
  placeholderText: {
    ...tokens.typography.label.large,
    color: Colors.role.textSoft,
  },
});
